import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import GlucoseCurveComparisonPanel from "./GlucoseCurveComparisonPanel";
import homeService, {
  type HomePageData,
  type InterventionResponse,
} from "../../services/HomeService";
import { useEnsureSessionReady } from "../../hooks/useEnsureSessionReady";
import { validateMealId } from "../../utils/homePageUtils";
import { buildInterventionChartData } from "../../utils/buildInterventionChartData";
import HealthInsightsHomeCard from "./HealthInsightsHomeCard";
import HomeBbsPillarsSummary from "./HomeBbsPillarsSummary";
import { gluceraBrandPrimaryButton } from "../../utils/gluceraTypography";

interface LatestMealSummary {
  meal_id?: string;
  meal_type?: string;
  meal_time?: string;
  title?: string;
}

interface GlucoseSpikeSummary {
  predicted_spike_mg_dl?: number;
  this_meal_spike_mg_dl?: number;
  baseline_glucose?: number;
  time_to_peak_min?: number;
  glucose_curve?: {
    points: Array<{ time_min: number; glucose_mg_dl: number }>;
  };
}

export interface ClosedLoopHomeSectionProps {
  homeData?: HomePageData;
  mealId?: string;
  latestMeal?: LatestMealSummary;
  glucoseSpikeData?: GlucoseSpikeSummary;
  activityKey?: string;
  activityLabel?: string;
  onClose?: () => void;
  onContinue?: () => void;
  onViewDetail?: () => void;
  selectedDate?: string;
  showScorePillars?: boolean;
}

const formatElapsedPostMeal = (
  mealTime?: string,
  mealType?: string
): string => {
  if (!mealTime) return "POST-MEAL";
  const mealDate = new Date(mealTime);
  if (Number.isNaN(mealDate.getTime())) return "POST-MEAL";
  const elapsedMs = Date.now() - mealDate.getTime();
  if (elapsedMs < 0) return "POST-MEAL";
  const hours = Math.floor(elapsedMs / 3_600_000);
  const mins = Math.floor((elapsedMs % 3_600_000) / 60_000);
  const label = (mealType || "meal").replace(/_/g, " ").toUpperCase();
  if (hours > 0) return `${hours}H ${mins}M POST-${label}`;
  return `${mins}M POST-${label}`;
};

const ClosedLoopHomeSection = ({
  homeData,
  mealId,
  latestMeal,
  glucoseSpikeData,
  activityKey = "walking",
  activityLabel: activityLabelProp,
  onClose,
  onContinue,
  onViewDetail,
  selectedDate,
  showScorePillars,
}: ClosedLoopHomeSectionProps) => {
  const { canUseAuthenticatedApis } = useEnsureSessionReady();
  const validMealId = validateMealId(mealId ?? null);

  const { data: apiData } = useQuery<InterventionResponse | null>({
    queryKey: ["closed-loop-intervention", validMealId, activityKey],
    queryFn: () =>
      homeService.calculateIntervention(validMealId!, activityKey),
    enabled: canUseAuthenticatedApis && Boolean(validMealId),
    staleTime: 5 * 60 * 1000,
  });

  const curvePoints = glucoseSpikeData?.glucose_curve?.points ?? [];
  const fbBaseline =
    glucoseSpikeData?.baseline_glucose ??
    curvePoints[0]?.glucose_mg_dl ??
    apiData?.now_glucose ??
    100;
  const fbSpike =
    glucoseSpikeData?.this_meal_spike_mg_dl ??
    glucoseSpikeData?.predicted_spike_mg_dl ??
    68;

  const noActionPeak =
    apiData?.original_peak ??
    curvePoints.reduce(
      (max, p) => (p.glucose_mg_dl > max ? p.glucose_mg_dl : max),
      fbBaseline + fbSpike
    );

  const withActionPeak =
    apiData?.new_peak ??
    Math.max(noActionPeak - (apiData?.intervention_drop_mg_dl ?? 26), fbBaseline);

  const dropMgDl = Math.round(
    apiData?.intervention_drop_mg_dl ?? noActionPeak - withActionPeak
  );

  const activityLabel =
    activityLabelProp ??
    apiData?.activity?.replace(/_/g, " ") ??
    "walk";

  const mealDisplayName =
    latestMeal?.title?.trim() ||
    latestMeal?.meal_type?.replace(/_/g, " ") ||
    "lunch";

  const postMealBadge = formatElapsedPostMeal(
    latestMeal?.meal_time,
    latestMeal?.meal_type
  );

  /* Pattern insight card — temporarily hidden
  const plateData = resolvePlateOptimisationData({
    homePlate: homeData?.plate_optimisation,
  });
  */

  // Next meal type: cycle after current meal
  const nextMealType = useMemo(() => {
    const current = (latestMeal?.meal_type ?? "").toLowerCase();
    if (current.includes("breakfast")) return "Lunch";
    if (current.includes("lunch")) return "Dinner";
    if (current.includes("dinner")) return "Breakfast";
    return "Dinner";
  }, [latestMeal?.meal_type]);

  const avgSpikeMgDl =
    homeData?.["Glucose Spikes"]?.daily_summary?.avg_this_meal_spike_mg_dl;

  /* Pattern insight card — temporarily hidden
  const protocolTitle = plateData?.headline
    ? plateData.headline
    : `15-min ${activityLabel} after ${mealDisplayName}`;

  const protocolSteps = useMemo(() => {
    if (plateData?.steps?.length) {
      return plateData.steps.slice(0, 2).map((s, i) => ({
        label: s.label,
        delta: i === 0 ? -12 : -18,
      }));
    }
    return [
      { label: "Meal logged with action taken", delta: -12 },
      { label: `15-min ${activityLabel} after meal`, delta: -Math.max(dropMgDl - 12, 8) },
    ];
  }, [plateData, activityLabel, dropMgDl]);
  */

  const chartData = useMemo(
    () => buildInterventionChartData(apiData, curvePoints, dropMgDl),
    [apiData, curvePoints, dropMgDl]
  );

  const nowGl =
    apiData?.now_glucose ??
    curvePoints[0]?.glucose_mg_dl ??
    fbBaseline;

  const activityDisplayLabel =
    apiData?.activity?.replace(/_/g, " ") ??
    activityLabel.replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="space-y-4 pb-0">
      {/* Top bar */}
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex max-w-[85%] items-center gap-2 rounded-full border border-emerald-200/80 bg-emerald-50 px-3 py-1.5 dark:border-emerald-800/50 dark:bg-emerald-950/40">
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
          <span className="text-compact-xs font-bold uppercase leading-tight tracking-wide text-emerald-800 dark:text-emerald-300">
            Window closed · {postMealBadge}
          </span>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close closed loop summary"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground transition-colors hover:bg-muted/50"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Hero */}
      <div className="space-y-2 px-0.5">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground">
          You bent it.
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          You changed the outcome — not just the score. Your peak stayed{" "}
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
            {dropMgDl} mg/dL below
          </span>{" "}
          where it was projected without action.
        </p>
      </div>

      {/* Peak comparison */}
      <Card className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <div className="h-1 bg-emerald-500" />
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-5">
          <div className="text-center">
            <p className="text-compact-xs font-bold uppercase tracking-wider text-muted-foreground">
              No action
            </p>
            <p className="mt-1 text-4xl font-black text-red-500 line-through decoration-2">
              {Math.round(noActionPeak)}
            </p>
            <p className="text-compact-xs font-semibold uppercase text-muted-foreground">
              mg/dL
            </p>
          </div>
          <span className="text-lg text-muted-foreground/60">→</span>
          <div className="text-center">
            <p className="text-compact-xs font-bold uppercase tracking-wider text-muted-foreground">
              Projected peak
            </p>
            <div className="mt-1 flex items-center justify-center gap-2">
              <p className="text-4xl font-black text-emerald-600">
                {Math.round(withActionPeak)}
              </p>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
                -{dropMgDl}
              </span>
            </div>
            <p className="text-compact-xs font-semibold uppercase text-muted-foreground">
              mg/dL · with {activityDisplayLabel}
            </p>
          </div>
        </div>
      </Card>

      {/* Chart — with YOUR CURVE header */}
      <Card className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4 pb-1">
          <span className="text-compact-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
            Your curve
          </span>
          <span className="text-compact-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
            {(() => {
              const totalMin = curvePoints[curvePoints.length - 1]?.time_min
                ?? glucoseSpikeData?.time_to_peak_min
                ?? 120;
              return `${Math.round(totalMin)} min · complete`;
            })()}
          </span>
        </div>
        <div className="px-4 pb-4">
        <GlucoseCurveComparisonPanel
          chartData={chartData}
          noActionPeak={noActionPeak}
          withActionPeak={withActionPeak}
          dropMgDl={dropMgDl}
          activityLabel={activityDisplayLabel}
          nowGl={nowGl}
          chartHeightClass="h-44"
          showPeakComparison={false}
        />
        </div>
      </Card>

      {/* Pattern insight — temporarily hidden
      <Card className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="space-y-4 p-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span className="text-compact-xs font-bold uppercase tracking-widest text-amber-500">
              Pattern insight
            </span>
          </div>
          <p className="text-lg leading-snug text-foreground">
            <span className="font-semibold text-amber-500">{protocolTitle}</span>
            <span className="italic text-muted-foreground"> — a pattern that works for you.</span>
          </p>
          <ul className="space-y-2.5">
            {protocolSteps.map((step) => (
              <li
                key={step.label}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="flex items-center gap-2 text-foreground">
                  <Check className="h-4 w-4 shrink-0 text-emerald-500" />
                  {step.label}
                </span>
                <span className="shrink-0 font-bold text-amber-500">{step.delta}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs italic leading-relaxed text-muted-foreground">
            <span className="font-semibold not-italic text-foreground">
              Bite Balance learns.
            </span>{" "}
            We&apos;ll suggest this protocol again on your next similar meal.
          </p>
        </div>
      </Card>
      */}

      {homeData?.["Health Insights"] ? (
        <HealthInsightsHomeCard
          healthInsights={homeData["Health Insights"]}
          onContinue={onContinue}
        />
      ) : null}

      {onContinue && !homeData?.["Health Insights"] ? (
        <Button
          type="button"
          onClick={onContinue}
          className={`h-11 w-full rounded-xl ${gluceraBrandPrimaryButton}`}
        >
          Back to home
        </Button>
      ) : null}

      <HomeBbsPillarsSummary
        homeData={homeData}
        selectedDate={selectedDate}
        showFourPillars={showScorePillars}
      />
    </div>
  );
};

export default ClosedLoopHomeSection;
